import { useState } from "react";
// Custom Liste Studyosu — adim tanimlari ve API yardimcisi.
// Adim metinleri kullaniciyi yonlendirir: ne yapilir, neden onemli.
import type { ApparelStudy, MockupAnalysis } from "@erc/shared";
import { authHeaders, getBackendUrl } from "./http";

export type StepKey = "source" | "study" | "template" | "samples" | "images" | "listing" | "publish";
export type StepStatus = "done" | "legacy" | "deferred" | "todo";

export interface StepState { key: StepKey; status: StepStatus; summary: string; missing: string[] }

export interface EtsyQuestion {
  type: string; question: string; instructions?: string; required: boolean;
  maxChars?: number; maxFiles?: number; options?: string[];
}

export type ChartKind = "design_numbers" | "ink_colors" | "font" | "how_to_order" | "photo_to_design";
export interface ChartSpec {
  kind: ChartKind; title: string; sample?: string; background?: "collage" | "plain"; accent?: string;
  cols?: number; tile?: "box" | "none" | "round"; mono?: boolean;
  items: Array<{ label: string; imageUrl?: string; hex?: string; font?: string }>;
}

export interface AnalysisCard {
  id: string; productType: string | null; title: string | null; url: string | null; imageUrls: string[];
  /** Satan listeden gelen SEO (yalniz studyo detayinda) */
  seo?: { title: string; tags: string[]; description: string };
}

export interface CustomListingDetail {
  id: string; name: string; updatedAt: string;
  source: { imageUrls?: string[]; questions?: EtsyQuestion[]; fetchedAt?: string };
  listing: { images?: string[]; imagesEdited?: boolean; mainImage?: string; listingTemplateId?: string; variationTemplateId?: string; mockupCollectionIds?: string[]; group?: { id: string; urls: string[] }; bigPrintUrls?: string[] };
  analysis: AnalysisCard | null;
  /** Urun tipi; giyim degilse (ornament, kupa) tisort tonlari ve Big print yok */
  productTypeKey?: string | null;
  isGarment?: boolean;
  /** Calisma bitmeden once yalniz status/analyzedAt/error tasiyabilir */
  study: (Partial<ApparelStudy> & Pick<ApparelStudy, "status">) | null;
  models: Array<{ id: string; label: string }>; defaultModel: string;
  template: {
    glyphSets?: Record<string, GlyphSetView>;
    base?: { status: "running" | "ready" | "failed"; url?: string; values?: Record<string, string>; referenceImageUrl?: string; model?: string; fidelity?: string; source?: "existing"; error?: string };
    mockupRenders?: Array<{ mockupId: string; label: string; url: string }>;
    /** Menuden secilen tasarimlarin ikon seti (4. adim) */
    iconSet?: { status?: "running" | "ready" | "failed"; subjects?: string[]; styleNote?: string; error?: string; sheetUrl?: string; icons: Array<{ label: string; subject: string; url?: string }> };
    charts?: Partial<Record<ChartKind, { url: string; spec: ChartSpec; updatedAt: string }>>;
    /** Elle silinen kartelalar (kendiliginden yeniden cizilmez) */
    chartsRemoved?: Partial<Record<ChartKind, boolean>>;
    collage?: { status: "running" | "ready" | "failed"; error?: string; url?: string; blank?: { url: string; mockupId: string; sourceUrl: string }; updatedAt: string };
    samples?: { rows: SampleRow[]; renders: Array<SampleRow & { url: string; fromBase?: boolean }>; updatedAt: string; mode?: "fast" | "template"; status?: "running" | "ready" | "failed"; error?: string; total?: number };
    pkg?: { status: "running" | "ready" | "failed"; error?: string; note?: string; template?: { artUrl: string; layers: Array<{ fieldKey?: string }>; photo?: { key: string; sampleUrl: string } } };
    samplePhotos?: { status: "running" | "ready" | "failed"; urls: string[]; total: number; error?: string; updatedAt: string };
    niches?: { items: NicheIdeaView[]; updatedAt: string };
    blankMockups?: Array<{ id: string; status: "running" | "ready" | "failed"; sourceUrl: string; count: number; mockupId?: string; url?: string; error?: string; updatedAt: string }>;
  };
  /** Harf seti stil referansi adaylari */
  refImages: string[];
  /** Bu analizin uretilmis/yuklenmis tasarimlari (hazir tasarimla sablona gecis) */
  designImages: string[];
  /** Calismanin istedigi kartelalarin varsayilan icerigi */
  chartDefaults: Partial<Record<ChartKind, ChartSpec>>;
  /** Murekkep kartelasi: secim (auto/none/id), uygulanan, tum kartelalar */
  inkPalette?: { choice: string; active: { id: string; name: string; via: "override" | "assigned" | "default"; count: number } | null; palettes: Array<{ id: string; name: string; count: number }> };
  imageModels: Array<{ id: string; label: string }>; defaultImageModel: string;
  steps: StepState[]; current: StepKey;
  links: { poolDesignId: string | null; draftId: string | null; etsyListingId: string | null };
}

export interface SampleRow { id: string; productColor: string; tone: "light" | "mid" | "dark"; values: Record<string, string> }

export interface GlyphSetView {
  status: "running" | "ready" | "failed"; error?: string; styleRefUrl: string;
  glyphs: Record<string, string>; cycleColors: string[]; missing: string[]; model: string; updatedAt: string;
}

export interface CustomListingRow {
  id: string; name: string; updatedAt: string; analysis: AnalysisCard; current: StepKey;
  steps: Array<{ key: StepKey; status: StepStatus }>;
  /** Uretim yolu (liste calismasi varsa) */
  production?: "template" | "ai" | null;
}

export const STEP_META: Record<StepKey, { n: number; title: string; what: string; why: string }> = {
  source: {
    n: 1, title: "Kaynak",
    what: "İlham listesinden AI'ın inceleyeceği fotoğrafları seç ve Etsy'deki kişiselleştirme sorularını çek.",
    why: "AI yalnız seçtiğin fotoğrafları görür ve her fotoğraf maliyeti artırır. Çoğu listede ana mockup ile varsa kartela (font, renk ya da tasarım numaraları) yeterlidir. Etsy soruları, alıcının neyi yazdığını ya da seçtiğini kesin olarak gösterir.",
  },
  study: {
    n: 2, title: "Liste çalışması",
    what: "AI ilham listesini Etsy yapısıyla çözer: hangi katman sabit, hangisini alıcı yazar ya da seçer; renkler tişört tonuna göre nasıl değişir; alıcıya hangi sorular sorulur; telif riski var mı.",
    why: "Sonraki adımların hepsi bu çalışmaya dayanır. Bir şey yanlışsa düzeltme notu yaz; not ders olarak kaydedilir ve sonraki listelerde aynı hata tekrarlanmaz.",
  },
  template: {
    n: 3, title: "Tasarım ve şablon",
    what: "Önce taban tasarım üretilir, sonra yazısı silinip düzenlenebilir şablona çevrilir.",
    why: "Şablon sayesinde alıcının yazacağı her isim aynı font ve stille kodla basılır. Her isim için yeniden AI çizimi gerekmez; sonuç tutarlı ve ucuz olur.",
  },
  samples: {
    n: 4, title: "Örnekler",
    what: "Şablondan örnek değerlerle (farklı isimler, sınıflar, renkler) tasarımlar basılır.",
    why: "Kolajda ve mockuplarda ürünün kişiselleştirilebildiği bu örneklerle görünür; alıcı farklı değerlerin nasıl duracağını görür.",
  },
  images: {
    n: 5, title: "Görseller",
    what: "Listenin görselleri Etsy'deki sırasıyla hazırlanır: satan mockup, kolaj, kartelalar, şablon görselleri (beden ve renk tablosu).",
    why: "İlk görsel aramada küçük resim olarak görünür; ilham listesinin ana fotoğrafıyla aynı sunum tipinde olmalı. Kolaj, custom ürünün farklı değerlerle nasıl görüneceğini anlatır.",
  },
  listing: {
    n: 6, title: "Liste",
    what: "Liste Denetimi düzeninde: liste şablonunu seç, görselleri sırala ve mockup ekle, engel ve uyarıları gider, sonra taslağa çevir. Başlık ve 13 etiket satan listeden, fiyat/kargo/iade/varyasyonlar liste şablonundan gelir.",
    why: "Liste şablonu sabit ayarları her listede aynı tutar; listeden listeye yalnız başlık, etiket, görseller ve kişiselleştirme değişir.",
  },
  publish: {
    n: 7, title: "Yayın",
    what: "Panel taslağı Etsy liste sayfası gibi gösterilir: görseller, fiyat, varyasyonlar, kişiselleştirme kutusu, açıklama ve etiketler. Son kontrolden sonra Listing Builder'dan yayınlanır ya da Zamanlayıcı'dan planlanır.",
    why: "Etsy taslağında son bir kez görseller ve kişiselleştirme soruları kontrol edilir; yayın zamanı Zamanlayıcı'dan ayarlanabilir.",
  },
};

export const STEP_ORDER: StepKey[] = ["source", "study", "template", "samples", "images", "listing", "publish"];

export const STATUS_LABEL: Record<StepStatus, string> = { done: "Tamam", legacy: "Eski sistemde", deferred: "Ertelendi", todo: "Bekliyor" };

export const QUESTION_TYPE_LABEL: Record<string, string> = {
  text_input: "Metin", dropdown: "Açılır menü", unlabeled_upload: "Dosya yükleme", labeled_upload: "Etiketli dosya yükleme",
};

export async function customApi<T>(method: string, path: string, body?: unknown): Promise<T> {
  const r = await fetch(`${getBackendUrl()}/api/custom-listings${path}`, {
    method, headers: authHeaders(), body: body === undefined ? undefined : JSON.stringify(body),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `İstek başarısız (${r.status})`);
  return j.data as T;
}

export interface StudioMockup {
  id: string; label: string; imageUrl: string; brand: string | null; model: string | null; color: string | null;
  isSelling: boolean; areaCount: number; analysis: MockupAnalysis | null;
  match: { score: number; reasonsTr: string[]; warningsTr: string[] };
}

export const PRESENTATION_LABEL: Record<string, string> = {
  model: "Manken", flat_lay: "Düz serili", folded: "Katlı", hanging: "Askıda", ghost: "Görünmez manken", other: "Diğer",
};
export const PRODUCT_TYPE_LABEL: Record<string, string> = {
  tshirt: "Tişört", sweatshirt: "Sweatshirt", hoodie: "Kapüşonlu", boxy_tee: "Boxy tee", long_sleeve: "Uzun kollu", tank: "Atlet", baby: "Bebek", other: "Diğer",
};

/** Studyoda gorsel modeli: son secim hatirlanir (tum adimlar ortak), yoksa sunucu varsayilani */
export function useStudioModel(rec: { imageModels: Array<{ id: string }>; defaultImageModel: string }): [string, (v: string) => void] {
  const KEY = "erc:model:custom-studio";
  const [m, setM] = useState(() => {
    try { const v = localStorage.getItem(KEY); if (v && rec.imageModels.some((x) => x.id === v)) return v; } catch { /* yok */ }
    return rec.defaultImageModel;
  });
  return [m, (v: string) => { setM(v); try { localStorage.setItem(KEY, v); } catch { /* yok */ } }];
}

/** Alan duzenleyicide denenecek ornek tasarimlar: "light" alan acik renkli tasarim ister
 *  (koyu tisort ornegi), "dark" alan koyu tasarim (acik tisort ornegi) */
export function studioTestDesigns(rec: CustomListingDetail): { testDesignLight: string | null; testDesignDark: string | null } {
  const r = rec.template.samples?.renders ?? [];
  return {
    testDesignLight: (r.find((x) => x.tone === "dark") ?? r[0])?.url ?? null,
    testDesignDark: (r.find((x) => x.tone !== "dark") ?? r[0])?.url ?? null,
  };
}

/** Mikro nis ailesi onerisi (sunucu NicheIdea) */
export interface NicheIdeaView {
  id: string; niche: string; values: Record<string, string>; title: string; tags: string[];
  replace: Array<[string, string]>; status?: "idle" | "running" | "ready" | "failed"; draftId?: string; error?: string;
}
